// "Complete your profile" on the organisation console (spec/07 §3.2, owner 2026-10-08): right after a staff member's
// first sign-in with Google, Discord or Steam, before any screen. A staff sign-up asks only for a name (with the
// email and password, which a provider stands in for), so this asks only for that, prefilled from the provider's:
// it is what the team and the activity log show. `POST /v1/me/profile/complete` saves it on the account and marks
// the profile complete; `GET /v1/me` then lets the member in (`routes/shared.tsx`).

import { useTranslation } from '@clavian/i18n/react';
import { Button, Skeleton } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';

import { useApi } from '../api/client';
import { queryKeys, useSession } from '../api/queries';
import { useProblem } from '../api/use-problem';
import { AuthLayout } from './AuthLayout';
import { Failure, TextInput } from './form-controls';
import { useOptionalSurface } from '../surface';
import { afterSignIn } from './invitation-token';

/** `GET /v1/me`, as the console keeps it: whether the profile still needs completing. */
export const meKey = ['me', 'profile'] as const;

export function WelcomePage(): ReactNode {
  const { t } = useTranslation('console');
  const user = useSession().data?.user;
  return (
    <AuthLayout title={t('welcome.title')} subtitle={t('welcome.intro')}>
      {user === undefined ? <Skeleton shape="text" lines={2} /> : <WelcomeForm name={user.name ?? ''} />}
    </AuthLayout>
  );
}

function WelcomeForm({ name }: { readonly name: string }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ from: '/welcome' });
  const surface = useOptionalSurface();
  const [failure, setFailure] = useState<string | null>(null);
  const form = useForm<{ name: string }>({ defaultValues: { name } });

  const submit = form.handleSubmit(async (values) => {
    setFailure(null);
    const chosen = values.name.trim();
    try {
      const profile = await api.completeMyProfile({ body: { displayName: chosen, name: chosen } });
      queryClient.setQueryData(meKey, profile);
      await queryClient.invalidateQueries({ queryKey: queryKeys.session });
      const landing = afterSignIn(search.redirect);
      if (surface?.afterProfileComplete !== undefined) window.location.assign(surface.afterProfileComplete(landing));
      else await navigate({ to: landing });
    } catch (error) {
      setFailure(problem.fieldMessage(error, 'displayName') ?? problem.fieldMessage(error, 'name') ?? problem.text(error, { fallback: t('welcome.failed') }));
    }
  });

  return (
    <form className="console-form" onSubmit={(event) => void submit(event)} noValidate aria-label={t('welcome.title')}>
      <Failure message={failure} />
      <TextInput
        control={form.control}
        name="name"
        label={t('welcome.name')}
        autoComplete="name"
        rules={{ required: t('signIn.required'), validate: (value: string) => value.trim() !== '' || t('signIn.required') }}
      />
      <Button type="submit" variant="primary" busy={form.formState.isSubmitting}>
        {t('welcome.submit')}
      </Button>
    </form>
  );
}
